import { assignInlineVars } from "@vanilla-extract/dynamic";
import { KeyboardEvent, ReactElement } from "react";

import { Tile } from "chameleon/lib/components/tile/tile";

import { Divider } from "../divider/divider";
import { PaymentMethodTitle } from "../payment-method-title/payment-method-title";
import { RadioTile } from "../radio-tile/radio-tile";
import { fieldsetClass, gapVar, radioGroupRootClass } from "./radio-group.css";

export interface RadioOption {
  id: string;
  type?: string;
  label?: string;
  labelComponent?: ReactElement;
  leadingSlot?: ReactElement;
  trailingSlot?: ReactElement;
  messages?: string[];
  componentType?: "radio" | "divider" | "noRadio";
}

export interface RadioGroupProps {
  /**
   * Gap between radio elements
   */
  gap?: string;
  /**
   * List of options
   */
  options: RadioOption[];
  /**
   * Selected option
   */
  selectedOption?: string;
  /**
   * children of selected option in radio group
   */
  children?: ReactElement;
  /**
   * Tab index
   */
  tabIndex?: number;
  /**
   * To show border around radio button
   */
  onChange: (s: string, type: string) => void;
  /**
   * isExpanded to control visibility of children from parent component, even if option is selected
   */
  isExpanded?: boolean;
  /**
   * Resetting animation whenever the string changes
   */
  resetAnimation?: string;
  /**
   * radio group name
   */
  name?: string;
}

export const RadioGroup = ({
  gap = "4",
  options,
  selectedOption,
  children,
  tabIndex,
  isExpanded = true,
  resetAnimation,
  onChange,
  name,
}: RadioGroupProps) => {
  const focusOption = (index: number) => {
    const optionId = `${options[index].id}Option`;
    const optionElement = document.getElementById(optionId);
    optionElement?.focus();
  };

  const handleArrowNavigation = (
    direction: "prev" | "next",
    currentIndex: number
  ) => {
    const newIndex = direction === "prev" ? currentIndex - 1 : currentIndex + 1;
    const isValidIndex =
      direction === "prev" ? newIndex >= 0 : newIndex < options.length;

    if (isValidIndex) {
      const { id, type } = options[newIndex];
      onChange(id, type ?? "");
      focusOption(newIndex);
    }
  };

  const handleTabNavigation = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.shiftKey) return;

    const currentElement = e.target as HTMLInputElement;
    const nextElement = currentElement?.nextElementSibling;
    if (!nextElement) return;

    // Find the focusable elements in the next sibling
    const focusableElements = [
      // When treatment-q3-bank-account-profiles is off
      nextElement.querySelector(
        "button[data-testid='btn-edit-payment-method']"
      ),
      // When treatment-q3-bank-account-profiles is on
      nextElement.querySelector("button[data-testid='flyout-menu']"),
    ].filter((element) => element instanceof HTMLElement) as HTMLElement[];

    // Focus the first available element if any exists
    if (focusableElements.length > 0) {
      e.preventDefault();
      focusableElements[0].focus();
    }
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    const currentElement = e.target as HTMLInputElement;
    const optionIds = options.map((option) => `${option.id}Option`);
    const index = options.findIndex((option) => option.id === selectedOption);

    if (!optionIds.includes(currentElement.id)) return;

    switch (e.code) {
      case "ArrowLeft":
      case "ArrowUp":
        handleArrowNavigation("prev", index);
        break;

      case "ArrowDown":
      case "ArrowRight":
        handleArrowNavigation("next", index);
        break;

      case "Tab":
        handleTabNavigation(e);
        break;

      default:
        return;
    }
  };

  const getOptionBody = (option: RadioOption) => {
    switch (option.componentType) {
      case "divider":
        return <Divider label={option.label} />;
      case "noRadio":
        return (
          <Tile
            id={`${option.id}tile`}
            title={
              <PaymentMethodTitle
                title={option.label}
                titleStyleVariant={"heading4"}
                titleComponent={option.labelComponent}
                leadingSlot={option.leadingSlot}
                trailingSlot={option.trailingSlot}
                forId={`${option.id}tile`}
                tagElement="label"
              />
            }
          >
            {selectedOption === option.id ? children : null}
          </Tile>
        );
      default:
        return (
          <RadioTile
            id={`${option.id}Option`}
            tabIndex={tabIndex}
            onChange={onChange}
            onKeyDown={onKeyDown}
            messages={option.messages}
            title={
              <PaymentMethodTitle
                title={option.label}
                titleStyleVariant={"heading4"}
                titleComponent={option.labelComponent}
                leadingSlot={option.leadingSlot}
                trailingSlot={option.trailingSlot}
                forId={`${option.id}Option`}
                tagElement="label"
              />
            }
            type={option.type}
            value={option.id}
            isSelected={option.id === selectedOption}
            showChildren={isExpanded}
            resetAnimation={resetAnimation}
            name={name}
          >
            {selectedOption === option.id ? children : null}
          </RadioTile>
        );
    }
  };

  return (
    <fieldset
      className={`${radioGroupRootClass} ${fieldsetClass}`}
      style={assignInlineVars({
        [gapVar]: gap,
      })}
      role="radiogroup"
    >
      {options.map((option) => {
        const key = `${option.id}Option`;
        return (
          <span key={key} data-dd-action-name={key}>
            {getOptionBody(option)}
          </span>
        );
      })}
    </fieldset>
  );
};

RadioGroup.displayName = "RadioGroup";
